/**
 * Pantalla 1 del §7: nueva investigación e historial.
 *
 * Lo que hace que esta pantalla funcione es el progreso en vivo: una corrida
 * tarda entre uno y varios minutos, así que hay que mostrar en qué fase va y
 * cuánto lleva, no una ruleta indefinida.
 */

import { Search, SlidersHorizontal } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ErrorApi } from '../api/cliente'
import { useEntidades, useNuevaInvestigacion, useSalud } from '../api/hooks'
import type { FiltrosBusqueda } from '../api/tipos'
import { FilaInvestigacion } from '../componentes/FilaInvestigacion'
import { ProgresoCorrida } from '../componentes/ProgresoCorrida'
import { LARGO_MINIMO, revisarNombre } from '../ui/nombre-entidad'
import { cx } from '../ui/util'
import {
  AreaTexto,
  Boton,
  Campo,
  Cargando,
  Entrada,
  Error as EstadoError,
  Interruptor,
  Panel,
  Seleccion,
  Vacio,
} from '../ui/primitivos'

const RECENCIAS = [
  { valor: '', etiqueta: 'Sin límite' },
  { valor: 'day', etiqueta: 'Último día' },
  { valor: 'week', etiqueta: 'Última semana' },
  { valor: 'month', etiqueta: 'Último mes' },
  { valor: 'year', etiqueta: 'Último año' },
]

function FiltrosAvanzados({
  filtros,
  onChange,
}: {
  filtros: FiltrosBusqueda
  onChange: (f: FiltrosBusqueda) => void
}) {
  return (
    <div className="grid gap-3 rounded-md border border-[var(--color-borde)] bg-[var(--color-lienzo)] p-3 sm:grid-cols-2">
      <Campo
        etiqueta="Dominios"
        ayuda="Máx. 20. Con «-» delante para excluir; no se pueden mezclar ambos modos."
      >
        <Entrada
          placeholder="sea.gob.cl, sma.gob.cl"
          value={(filtros.dominios ?? []).join(', ')}
          onChange={(e) =>
            onChange({
              ...filtros,
              dominios: e.target.value
                .split(',')
                .map((d) => d.trim())
                .filter(Boolean),
            })
          }
        />
      </Campo>

      <Campo etiqueta="Recencia" ayuda="Acota la antigüedad de las fuentes consultadas.">
        <Seleccion
          value={filtros.recencia ?? ''}
          onChange={(e) =>
            onChange({
              ...filtros,
              recencia: (e.target.value || null) as FiltrosBusqueda['recencia'],
            })
          }
        >
          {RECENCIAS.map((r) => (
            <option key={r.valor} value={r.valor}>
              {r.etiqueta}
            </option>
          ))}
        </Seleccion>
      </Campo>

      <Campo
        etiqueta="Profundidad de búsqueda"
        ayuda="Más contexto encuentra más fuentes, tarda más y cuesta más."
      >
        <Seleccion
          value={filtros.contexto_busqueda ?? 'high'}
          onChange={(e) =>
            onChange({
              ...filtros,
              contexto_busqueda: e.target.value as FiltrosBusqueda['contexto_busqueda'],
            })
          }
        >
          <option value="low">Baja</option>
          <option value="medium">Media</option>
          <option value="high">Alta</option>
        </Seleccion>
      </Campo>

      <Campo etiqueta="Máximo de resultados" ayuda="Tope de fuentes por búsqueda (1-50).">
        <Entrada
          type="number"
          min={1}
          max={50}
          value={filtros.max_resultados ?? ''}
          onChange={(e) =>
            onChange({
              ...filtros,
              max_resultados: e.target.value ? Number(e.target.value) : undefined,
            })
          }
        />
      </Campo>
    </div>
  )
}

export function Inicio() {
  const navegar = useNavigate()
  const salud = useSalud()
  const [verDescartadas, setVerDescartadas] = useState(false)
  const entidades = useEntidades()

  const todas = entidades.data?.results ?? []
  // El interruptor solo se ofrece si hay algo que revelar: un control que no
  // cambia nada es ruido.
  const hayDescartadas = todas.some((e) => e.estado === 'DESCARTADO')
  const visibles = verDescartadas ? todas : todas.filter((e) => e.estado !== 'DESCARTADO')
  const investigar = useNuevaInvestigacion()

  const [nombre, setNombre] = useState('')
  const [contexto, setContexto] = useState('')
  const [paisRegion, setPaisRegion] = useState('')
  const [forzar, setForzar] = useState(false)
  const [avanzados, setAvanzados] = useState(false)
  const [filtros, setFiltros] = useState<FiltrosBusqueda>({ contexto_busqueda: 'high' })
  const [corridaId, setCorridaId] = useState<number | null>(null)
  const [entidadId, setEntidadId] = useState<number | null>(null)
  // Un nombre dudoso no se bloquea: se avisa y hay que insistir. «SQM» y
  // «BHP» son nombres reales, pero «super» devolvió una certificadora de
  // Estados Unidos y «co» tumbó la corrida entera.
  const [confirmado, setConfirmado] = useState(false)

  const sinKey = salud.data && !salud.data.api_key_cargada

  const revision = revisarNombre(nombre)
  const bloqueado = revision.nivel === 'invalido'
  const hayQueConfirmar = revision.nivel === 'dudoso' && !confirmado

  function enviar(evento: React.FormEvent) {
    evento.preventDefault()
    if (!nombre.trim() || bloqueado || hayQueConfirmar) return
    investigar.mutate(
      {
        nombre: revision.limpio,
        contexto: contexto.trim() || undefined,
        pais_region: paisRegion.trim() || undefined,
        filtros: avanzados ? filtros : undefined,
        forzar,
        confirmado: revision.nivel === 'dudoso' ? true : undefined,
      },
      {
        onSuccess: (corrida) => {
          setEntidadId(corrida.entity)
          // Una corrida reutilizada de la caché ya está lista: no hay nada que
          // sondear, se entra directo al workspace.
          if (corrida.reutilizada || corrida.terminada) {
            navegar(`/entidad/${corrida.entity}`)
          } else {
            setCorridaId(corrida.id)
          }
        },
      },
    )
  }

  // Al terminar la corrida se entra al workspace sin pedir otro clic.
  const alTerminar = () => {
    if (entidadId) navegar(`/entidad/${entidadId}`)
  }

  const errorValidacion =
    investigar.error instanceof ErrorApi ? investigar.error.errors : undefined

  return (
    <div className="mx-auto grid max-w-5xl gap-5 p-4 sm:p-6">
      <section>
        <h1 className="text-[20px] font-semibold tracking-tight">
          Inteligencia de stakeholders
        </h1>
        {/* Sin `max-w`: la tenía a 2xl por longitud de línea, pero el resto
            de la página va a 5xl y el corte antes de tiempo se leía como un
            defecto de alineación, no como una decisión. */}
        <p className="mt-1 text-[13px] text-[var(--color-texto-suave)]">
          Escribe el nombre de una empresa, proyecto o institución. StakeMap investiga sus
          stakeholders externos con fuentes citables y arma el grafo de relaciones.
        </p>
      </section>

      {sinKey && (
        <div className="rounded-md border border-[var(--color-critica)] bg-[color-mix(in_srgb,var(--color-critica)_8%,transparent)] px-3 py-2 text-[13px]">
          No hay API key cargada. Escribe la key de Perplexity en{' '}
          <code className="rounded bg-[var(--color-acento-suave)] px-1">apikey.txt</code> o
          exporta{' '}
          <code className="rounded bg-[var(--color-acento-suave)] px-1">
            PERPLEXITY_API_KEY
          </code>{' '}
          y recarga.
        </div>
      )}

      <Panel titulo="Nueva investigación">
        <form onSubmit={enviar} className="grid gap-3 p-3">
          <div className="grid gap-3 sm:grid-cols-[2fr_1fr]">
            <Campo etiqueta="Entidad a investigar">
              <Entrada
                autoFocus
                required
                minLength={LARGO_MINIMO}
                maxLength={200}
                placeholder="BHP Cerro Colorado"
                value={nombre}
                aria-invalid={bloqueado || undefined}
                aria-describedby={revision.mensaje ? 'aviso-nombre' : undefined}
                onChange={(e) => {
                  setNombre(e.target.value)
                  // Al cambiar el texto, la confirmación anterior ya no vale:
                  // se dio para otro nombre.
                  setConfirmado(false)
                }}
              />
              {revision.mensaje && (
                <p
                  id="aviso-nombre"
                  className={cx(
                    'mt-1.5 text-[12px]',
                    bloqueado ? 'text-[var(--color-opositora)]' : 'text-[var(--color-critica)]',
                  )}
                >
                  {revision.mensaje}
                </p>
              )}
              {hayQueConfirmar && (
                <label className="mt-1.5 flex cursor-pointer items-center gap-2 text-[12px]">
                  <input
                    type="checkbox"
                    checked={confirmado}
                    onChange={(e) => setConfirmado(e.target.checked)}
                  />
                  Buscarlo así de todos modos
                </label>
              )}
            </Campo>
            <Campo etiqueta="País o región" ayuda="Opcional; si se omite, se infiere.">
              <Entrada
                placeholder="Chile, Región de Tarapacá"
                value={paisRegion}
                onChange={(e) => setPaisRegion(e.target.value)}
              />
            </Campo>
          </div>

          <Campo
            etiqueta="Contexto adicional"
            ayuda="Opcional. Qué te interesa del caso, o qué ya sabes."
          >
            <AreaTexto
              rows={2}
              maxLength={2000}
              placeholder="Proyecto de extensión en evaluación ambiental; interesa el conflicto por agua."
              value={contexto}
              onChange={(e) => setContexto(e.target.value)}
            />
          </Campo>

          <div className="flex flex-wrap items-center gap-3">
            <Boton
              type="button"
              variante="sutil"
              icono={<SlidersHorizontal size={14} />}
              onClick={() => setAvanzados((v) => !v)}
            >
              {avanzados ? 'Ocultar opciones avanzadas' : 'Opciones avanzadas'}
            </Boton>
            <Interruptor activo={forzar} onChange={setForzar}>
              Forzar nueva búsqueda
              <span className="text-[var(--color-texto-tenue)]">
                (ignora corridas de menos de 24 h)
              </span>
            </Interruptor>
          </div>

          {avanzados && <FiltrosAvanzados filtros={filtros} onChange={setFiltros} />}

          {investigar.isError && <EstadoError error={investigar.error} />}
          {errorValidacion && (
            <ul className="rounded-md border border-[var(--color-opositora)] px-3 py-2 text-[12px]">
              {Object.entries(errorValidacion).map(([campo, mensajes]) => (
                <li key={campo}>
                  <strong>{campo}:</strong> {mensajes.join(' ')}
                </li>
              ))}
            </ul>
          )}

          <div className="flex items-center gap-3">
            <Boton
              type="submit"
              variante="primario"
              icono={<Search size={14} />}
              cargando={investigar.isPending}
              disabled={!nombre.trim() || bloqueado || hayQueConfirmar || corridaId !== null}
            >
              Investigar
            </Boton>
            <span className="text-[12px] text-[var(--color-texto-tenue)]">
              Suele tardar entre 1 y 6 minutos.
            </span>
          </div>
        </form>
      </Panel>

      {corridaId && (
        <ProgresoCorrida
          corridaId={corridaId}
          alTerminar={alTerminar}
          alCancelar={() => setCorridaId(null)}
        />
      )}

      <Panel
        titulo="Investigaciones previas"
        acciones={
          hayDescartadas || verDescartadas ? (
            <Interruptor activo={verDescartadas} onChange={setVerDescartadas}>
              Ver descartadas
            </Interruptor>
          ) : undefined
        }
      >
        {entidades.isLoading ? (
          <Cargando />
        ) : entidades.isError ? (
          <EstadoError error={entidades.error} reintentar={() => entidades.refetch()} />
        ) : !visibles.length ? (
          // Decir «todavía no hay» sería falso si están todas descartadas.
          <Vacio
            titulo={
              hayDescartadas ? 'Todas están descartadas' : 'Todavía no hay investigaciones'
            }
            detalle={
              hayDescartadas
                ? 'Activa «Ver descartadas» para recuperar alguna.'
                : 'La primera que lances aparecerá aquí para volver a abrirla cuando quieras.'
            }
          />
        ) : (
          <ul className="divide-y divide-[var(--color-borde)]">
            {visibles.map((e) => (
              <FilaInvestigacion key={e.id} entidad={e} />
            ))}
          </ul>
        )}
      </Panel>
    </div>
  )
}
